/**
 * Which planned work an approved absence actually takes away (manager redesign, 2026-10-09;
 * coverage audit P1). By instant, not by date: an absence 09–12 touches a 08–16 shift for three
 * hours and a 14–22 shift not at all; an overnight shift is touched by an absence after midnight.
 * Only the affected interval needs cover; the rest of the person's shift stays theirs.
 */
import type { ApiAbsence, ApiShift } from '@/api/data';
import { osloISO } from '@/rules';
import { shiftSpan } from '@/ui/format';
import { addDays } from '@timefly/core';
import type { Kind } from '../fravaer/AbsenceRange';
import { rangeOf } from '../fravaer/AbsenceRange';
import { workOf } from '../vaktplan/staffing';

export interface Affected {
  absence: ApiAbsence;
  shift: ApiShift;
  /** the part of the shift inside the absence, as instants */
  from: string;
  to: string;
}

/** The absence as an interval: whole days run from the first midnight to the midnight after the last day (the end date is a day off, ruling 2). */
export function absenceSpan(
  a: Pick<ApiAbsence, 'start_date' | 'end_date'>,
  kind: Kind,
): [number, number] | null {
  const r = rangeOf(a);
  if (!r.from) return null;
  if (kind === 'date')
    return [Date.parse(osloISO(r.from, '00:00')), Date.parse(osloISO(addDays(r.to || r.from, 1), '00:00'))];
  const a0 = Date.parse(a.start_date);
  const b0 = Date.parse(a.end_date);
  return Number.isNaN(a0) || Number.isNaN(b0) || b0 <= a0 ? null : [a0, b0];
}

/** A shift and an absence share time: a shift ending as the absence starts does not (review of #147). */
export const overlaps = (span: [number, number] | null, s: ApiShift): boolean =>
  !!span && Date.parse(s.start_time) < span[1] && Date.parse(s.end_time) > span[0];

/**
 * Each of the absent person's shifts the absence overlaps, cut to the overlap. The shifts carry no
 * link to a replacement, so cover counts as arranged only when it is unmistakable: an open shift
 * for exactly that interval, or someone else's shift for exactly a part of a shift (a partial
 * absence's gap). A colleague whose normal shift happens to have the same hours as a whole absent
 * shift is not taken as cover; that case is listed, and the manager judges it.
 * ponytail: a heuristic until shifts record what they replace; then match on the link.
 */
export function affected(a: ApiAbsence, kind: Kind, shifts: ApiShift[]): Affected[] {
  const span = absenceSpan(a, kind);
  if (!span || a.status !== 'accepted') return [];
  const [a0, b0] = span;
  const out: Affected[] = [];
  for (const s of shifts) {
    if (s.user_id !== a.user_id) continue;
    const from = Math.max(a0, Date.parse(s.start_time));
    const to = Math.min(b0, Date.parse(s.end_time));
    if (to <= from) continue;
    const partial = from !== Date.parse(s.start_time) || to !== Date.parse(s.end_time);
    const covered = shifts.some(
      (o) =>
        o.id !== s.id &&
        o.user_id !== a.user_id &&
        Date.parse(o.start_time) === from &&
        Date.parse(o.end_time) === to &&
        (o.user_id == null || partial),
    );
    if (!covered)
      out.push({ absence: a, shift: s, from: new Date(from).toISOString(), to: new Date(to).toISOString() });
  }
  return out;
}

/** "10:00–14:00", or across midnight "15.10 22:00–16.10 02:00" (#271). */
export function spanText(x: Pick<Affected, 'from' | 'to'>): string {
  return x.from && x.to ? shiftSpan(x.from, x.to) : '';
}

/**
 * The staffing Oversikt counts (#273 chart v3.1, OV3-05): published assigned shifts only, a draft is
 * not cover until it is published, and the part of a shift an approved absence takes is cut out. Planned
 * lunches stay in, as workOf keeps them. `absences` null: not readable, so nothing is cut (the page says so).
 */
export function staffWork(
  shifts: ApiShift[],
  absences: ApiAbsence[] | null,
  kindOf: (a: ApiAbsence) => Kind,
) {
  const out: ReturnType<typeof workOf> = [];
  const spans = (absences ?? [])
    .filter((a) => a.status === 'accepted')
    .map((a) => ({ u: a.user_id, span: absenceSpan(a, kindOf(a)) }));
  for (const s of shifts) {
    if (s.user_id == null || !s.published) continue;
    const [w] = workOf([s]);
    if (!w) continue;
    let parts: [number, number][] = [[w.a, w.b]];
    for (const x of spans) {
      if (x.u !== s.user_id || !x.span) continue;
      const [p, q] = x.span;
      parts = parts.flatMap(([a, b]): [number, number][] =>
        (
          [
            [a, Math.min(b, p)],
            [Math.max(a, q), b],
          ] as [number, number][]
        ).filter(([c, d]) => d > c),
      );
    }
    for (const [a, b] of parts) out.push({ ...w, a, b });
  }
  return out;
}
